import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useCallback } from "react";

export type MeritEnginePage =
  | "dashboard"
  | "load-price"
  | "resources"
  | "merit-order-dispatch"
  | "classes"
  | "bid-creation"
  | "bid"
  | "simulation-generation"
  | "archive";

export interface MeritEngineNavigationParams {
  page: MeritEnginePage;
  date?: string;
  session?: string;
  tab?: string;
  [key: string]: string | undefined;
}

export interface ModSessionPayload {
  stage?: unknown;
  date?: unknown;
  session?: unknown;
  version?: unknown;
  session_token?: unknown;
  [key: string]: unknown;
}

const STAGE_PAGE_MAP: Record<string, MeritEnginePage> = {
  LOAD: "load-price",
  GENERATION: "resources",
  MOD: "merit-order-dispatch",
  CLASSES: "classes",
  CLASS: "classes",
  CLASS_MOD: "classes",
  BID_CREATION: "bid-creation",
  CLASS_BID: "bid-creation",
  BID: "bid",
  SINGLE_BID: "bid",
};

export const normalizeSessionStage = (stage: unknown): string => {
  if (typeof stage !== "string") return "";
  return stage.trim().toUpperCase();
};

export const resolveMeritPageFromStage = (
  stage: unknown,
): MeritEnginePage | null => {
  const normalizedStage = normalizeSessionStage(stage);
  return STAGE_PAGE_MAP[normalizedStage] ?? null;
};

const hasValue = (value: unknown) => value !== null && value !== undefined;

export const resolveMeritPageFromSessionPayload = (
  payload: ModSessionPayload,
): MeritEnginePage | null => {
  const stagePage = resolveMeritPageFromStage(payload.stage);
  if (stagePage) return stagePage;

  // Backward-compatible fallback when stage is absent but payload has bid blocks.
  if (hasValue(payload.class_bid) || hasValue(payload.classBid)) {
    return "bid-creation";
  }
  if (hasValue(payload.single_bid) || hasValue(payload.bid)) {
    return "bid";
  }

  return null;
};

export const unwrapModSessionPayload = (
  response: unknown,
): ModSessionPayload => {
  if (!response || typeof response !== "object") {
    return {};
  }

  const root = response as Record<string, unknown>;
  const nestedData = root.data;

  if (
    nestedData &&
    typeof nestedData === "object" &&
    !Array.isArray(nestedData)
  ) {
    return nestedData as ModSessionPayload;
  }

  return root as ModSessionPayload;
};

export const useMeritEngineNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const getCurrentPage = useCallback((): MeritEnginePage | null => {
    const page = searchParams.get("tab");
    return (page as MeritEnginePage) || null;
  }, [searchParams]);

  const getCurrentParams = useCallback((): MeritEngineNavigationParams => {
    const params: MeritEngineNavigationParams = {
      page: getCurrentPage() || "dashboard",
    };

    // Add all search parameters
    searchParams.forEach((value, key) => {
      if (key !== "tab") {
        params[key] = value;
      }
    });

    return params;
  }, [searchParams, getCurrentPage]);

  const navigateToPage = useCallback(
    (page: MeritEnginePage, additionalParams: Record<string, string> = {}) => {
      const currentParams = new URLSearchParams(searchParams);

      // Set the page parameter
      currentParams.set("tab", page);

      // Add or update additional parameters
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value) {
          currentParams.set(key, value);
        } else {
          currentParams.delete(key);
        }
      });

      // Navigate to the same route with updated query parameters
      navigate(`${location.pathname}?${currentParams.toString()}`, {
        replace: false,
      });
    },
    [navigate, location.pathname, searchParams],
  );

  const updateParams = useCallback(
    (params: Record<string, string>) => {
      const currentParams = new URLSearchParams(searchParams);

      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          currentParams.set(key, value);
        } else {
          currentParams.delete(key);
        }
      });

      navigate(`${location.pathname}?${currentParams.toString()}`, {
        replace: true,
      });
    },
    [navigate, location.pathname, searchParams],
  );

  const goToPreviousStage = useCallback(
    async (getModSession: () => Promise<any>) => {
      try {
        const res = await getModSession();
        if (res) {
          const payload = unwrapModSessionPayload(res);
          const page = resolveMeritPageFromSessionPayload(payload);

          if (page) {
            navigateToPage(page);
          } else {
            alert("Error in continue to previous Session");
          }
        } else {
          alert("Error in continue to previous Session");
        }
      } catch (error) {
        console.error("Error getting previous session:", error);
        alert("Error in continue to previous Session");
      }
    },
    [navigateToPage],
  );

  const goToDashboard = useCallback(() => {
    navigateToPage("dashboard");
  }, [navigateToPage]);

  return {
    getCurrentPage,
    getCurrentParams,
    navigateToPage,
    updateParams,
    goToPreviousStage,
    goToDashboard,
    currentPage: getCurrentPage(),
    currentParams: getCurrentParams(),
  };
};
